/**
 * CPU implementation of the full simulation tick.
 *
 * Used for two things:
 *  1. the fallback backend when WebGPU is unavailable (lower resolutions);
 *  2. the offline reference that `test/` validates — mass conservation, no
 *     negative depths, no NaN, emergence of channels and talus slopes.
 *
 * The pass order matches `gpu/gpuBackend.ts` exactly:
 *
 *   WaterCycle  -> HydraulicErosion -> Weathering -> ThermalErosion -> WindErosion
 *
 * `WaterCycle` folds rainfall, pipe-flow, semi-Lagrangian advection, velocity
 * recovery and evaporation into one sub-stepped sweep: they all read the same
 * immutable pre-step state, so splitting them would only add buffer traffic.
 */

import { F, MAX_CELL_WATER, STATE_STRIDE } from './constants.ts';
import type { SimParams } from '../config.ts';
import { generateTerrain, stateIndex } from '../terrain/generator.ts';
import {
  MAX_SEDIMENT_CONCENTRATION,
  MAX_WATER_SPEED,
  REFERENCE_DT,
  WIND_THRESHOLD,
  accumulationBoost,
  capacityRelaxation,
  clamp,
  faceVelocity,
  flowConductivity,
  sedimentCapacityLoad,
  scaledSubsteps,
  talusLimit,
  thermalIterationMultiplier,
  thermalOutflow,
  timeScaledFraction,
  windDepositFraction,
  windEntrainment,
  windShelter,
} from './kernels.ts';
import { applyBrushToState } from './brush.ts';
import type { BrushStroke, SimBackend, SimStats } from './types.ts';

export class CpuBackend implements SimBackend {
  readonly kind = 'cpu' as const;
  readonly width: number;
  readonly height: number;

  #params: SimParams;
  private state: Float32Array;
  /** Post-generation snapshot; `resetToInitial` restores exactly this. */
  private initial: Float32Array;
  private scratch: Float32Array;

  private tickCount = 0;
  private simTime = 0;
  private fps = 0;
  private frameTimeMs = 0;
  private simMs = 0;
  private lastFrameStamp = 0;
  /** Extreme-slope scan is O(n); cache it for sixteen ticks unless host-edited. */
  private thermalMultiplier = 1;
  private nextThermalScanTick = 0;
  private thermalScanDirty = true;
  private readonly upwindScratch = new Float32Array(3);
  /** Reused (i, j, amount) work list for the wind pass; never reallocated. */
  private readonly movedScratch: number[] = [];
  /**
   * Per-*face* water flux [m of depth moved across that face this tick].
   *
   * The flux has to live on faces, not on cells: a boundary is shared by exactly
   * two cells, so a cell-indexed flux field aliases two different faces onto one
   * slot, and the two cells then disagree about how much water crosses. Storing
   * it per face makes "what the donor loses" and "what the receiver gains" the
   * same number by construction, which is the only way the transport is exactly
   * conservative.
   *
   * `faceX[i, j]` is the face between (i, j) and (i+1, j); the last column is
   * unused. `faceY` is analogous along y.
   */
  private faceX: Float32Array;
  private faceY: Float32Array;
  /** Sum of all positive MFD slopes leaving each donor, reused by accumulation. */
  private flowSlopeSum: Float32Array;
  /** Diagnostic latch: print the first non-finite flux and the inputs that made it. */
  debugFlux = false;

  constructor(params: SimParams) {
    this.#params = params;
    this.width = params.terrain.resolution;
    this.height = params.terrain.resolution;
    const n = this.width * this.height * STATE_STRIDE;
    this.state = new Float32Array(n);
    this.scratch = new Float32Array(n);
    this.faceX = new Float32Array(this.width * this.width);
    this.faceY = new Float32Array(this.width * this.width);
    this.flowSlopeSum = new Float32Array(this.width * this.width);
    this.initial = generateTerrain(params.terrain);
    this.state.set(this.initial);
  }

  get params(): SimParams {
    return this.#params;
  }

  /**
   * Live view of the packed state. Only the CPU backend can expose this; the
   * GPU backend must go through `readState()`.
   */
  get stateView(): Float32Array {
    return this.state;
  }

  setParams(params: SimParams): void {
    const oldResolution = this.#params.terrain.resolution;
    const oldCell = this.#params.terrain.cellSize;
    this.#params = params;
    this.thermalScanDirty = true;
    if (params.terrain.resolution !== oldResolution || params.terrain.cellSize !== oldCell) {
      this.regenerate();
    }
  }

  regenerate(): void {
    this.initial = generateTerrain(this.#params.terrain);
    this.state.set(this.initial);
    this.tickCount = 0;
    this.simTime = 0;
    this.thermalScanDirty = true;
  }

  resetToInitial(): void {
    this.state.set(this.initial);
    this.tickCount = 0;
    this.simTime = 0;
    this.thermalScanDirty = true;
  }

  invalidate(): void {
    /* nothing GPU-side to invalidate */
  }

  /* ---------------------------------------------------------------------- */
  /* Tick                                                                   */
  /* ---------------------------------------------------------------------- */

  tick(ticks: number): void {
    const t0 = performance.now();
    for (let t = 0; t < ticks; t++) {
      if (this.#params.hydraulic.enabled) this.waterCycle();
      if (this.#params.hydraulic.enabled) this.flowAccumulation();
      if (this.#params.hydraulic.enabled) this.hydraulicErosion();
      if (this.#params.hydraulic.enabled) this.weathering();
      if (this.#params.gravity.enabled) this.thermalErosion();
      if (this.#params.wind.enabled) this.windErosion();
      this.clampState();
      this.tickCount++;
      this.simTime += this.#params.engine.dtSim;
    }
    this.simMs = performance.now() - t0;
  }

  /**
   * Final non-negativity and boundedness guard for one tick.
   *
   * Every individual pass is written to be non-negative and conserving, and
   * under the default parameters it is. But erosion is a *feedback* process:
   * sediment capacity depends on speed, speed depends on the surface, and the
   * surface depends on erosion. With every parameter pushed to its limit
   * (`erosionStrength` 4, `maxErosionPerTick` 3, `dtSim` 8) that loop can still
   * self-amplify until a cell holds an absurd water column, and once one cell is
   * non-physical its neighbours inherit it.
   *
   * A cheap bounded sweep at the end of the tick keeps a bad configuration from
   * destroying the run. It is a guard-rail, not part of the physics: if it ever
   * fires under default settings, that is a bug worth chasing rather than a
   * number to tune away.
   */
  private clampState(): void {
    const s = this.state;
    const w = this.width;
    for (let k = 0; k < w * w; k++) {
      const b = k * STATE_STRIDE;

      // Terrain: elevations are relative to the datum, so only the floor is
      // meaningful. A non-finite value is reset to zero rather than propagated.
      let h = s[b + F.height];
      if (!Number.isFinite(h) || h < 0) h = 0;
      s[b + F.height] = h;

      // Water. Note the ceiling is *only* applied to non-finite values, not to
      // merely large ones: redistributing a "too deep" cell elsewhere is not
      // conservative in any useful sense — it moved water into dry, high ground
      // (including the test tank's walls) and made a level lake look unstable.
      // `MAX_CELL_WATER` now only guards against a genuine numerical blow-up,
      // and `totalWater` diagnostics would catch it if it ever fired.
      let water = s[b + F.water];
      if (!Number.isFinite(water) || water < 0) {
        water = 0;
      } else if (water > MAX_CELL_WATER) {
        water = MAX_CELL_WATER;
      }
      s[b + F.water] = water;

      // Columns and concentrations, re-derived so the invariants hold exactly
      let soil = s[b + F.soil];
      if (!Number.isFinite(soil) || soil < 0) soil = 0;
      if (soil > h) soil = h;
      s[b + F.soil] = soil;
      s[b + F.bedrock] = h - soil;

      let sed = s[b + F.sediment];
      if (!Number.isFinite(sed) || sed < 0) sed = 0;
      const cap = MAX_SEDIMENT_CONCENTRATION;
      if (sed > cap) sed = cap;
      s[b + F.sediment] = sed;

      // Velocities, fluxes and per-tick diagnostics
      const u = s[b + F.velU];
      s[b + F.velU] = Number.isFinite(u) ? Math.max(-MAX_WATER_SPEED, Math.min(MAX_WATER_SPEED, u)) : 0;
      const v = s[b + F.velV];
      s[b + F.velV] = Number.isFinite(v) ? Math.max(-MAX_WATER_SPEED, Math.min(MAX_WATER_SPEED, v)) : 0;
      const fx = s[b + F.fluxX];
      s[b + F.fluxX] = Number.isFinite(fx) ? fx : 0;
      const fy = s[b + F.fluxY];
      s[b + F.fluxY] = Number.isFinite(fy) ? fy : 0;
      const er = s[b + F.erosion];
      s[b + F.erosion] = Number.isFinite(er) ? Math.max(0, er) : 0;
      const dp = s[b + F.deposition];
      s[b + F.deposition] = Number.isFinite(dp) ? Math.max(0, dp) : 0;
      const hard = s[b + F.hardness];
      s[b + F.hardness] = Number.isFinite(hard) ? Math.max(0.25, Math.min(3, hard)) : 1;
      const windExport = s[b + F.windExport];
      s[b + F.windExport] = Number.isFinite(windExport) ? Math.max(0, windExport) : 0;
      const flowAccum = s[b + F.flowAccum];
      s[b + F.flowAccum] = Number.isFinite(flowAccum) ? Math.max(1, Math.min(w * w, flowAccum)) : 1;
      const groundwater = s[b + F.groundwater];
      s[b + F.groundwater] = Number.isFinite(groundwater) ? Math.max(0, groundwater) : 0;
    }
  }

  private get cellSize(): number {
    return this.#params.terrain.cellSize;
  }

  /* ---------------------------------------------------------------------- */
  /* Pass 1 — water cycle                                                   */
  /* ---------------------------------------------------------------------- */

  /**
   * Virtual-pipe shallow water + conservative suspended-load advection.
   *
   * `waterAdvect` evaluates each shared face once and then applies those fluxes
   * in a second sweep, restricted to water and sediment so the full-state traffic
   * stays confined to one scratch buffer. Velocity diagnostics come from the
   * accepted face fluxes rather than a redundant preliminary grid scan.
   *
   * The advection is a *donor-cell (upwind) flux* transport of
   *    water and suspended sediment. Unlike a semi-Lagrangian gather, every
   *    outgoing mass is exactly the mass some neighbour receives, so internal
   * transport is conservative to floating point. Rain and evaporation are
   * explicit sources/sinks, and boundary outflow is the catchment's outlet.
   * Sub-cycling keeps the donor cell stable at any dt.
   */
  private waterCycle(): void {
    this.waterAdvect();
    this.settleSaturatedLoad();
  }

  /**
   * Settle any suspended load that exceeds what the water can physically hold.
   *
   * The porosity limit (`MAX_SEDIMENT_CONCENTRATION`) has to be enforced during
   * advection, but simply clipping the concentration there would *destroy* the
   * excess mass. Converting it back into terrain instead is both conservative
   * and the right physics: an overloaded flow drops its load on the spot, which
   * is exactly how a debris flow leaves a lobe behind when it loses water.
   *
   * Run as its own sequential pass, after the advection sub-steps, so a cell is
   * corrected exactly once per tick no matter how many sub-steps it took part in.
   */
  private settleSaturatedLoad(): void {
    const s = this.state;
    const w = this.width;
    const cap = MAX_SEDIMENT_CONCENTRATION;
    for (let k = 0; k < w * w; k++) {
      const b = k * STATE_STRIDE;
      const water = s[b + F.water];
      const sediment = s[b + F.sediment];
      if (!(sediment > cap)) continue;
      const excess = sediment - cap;
      const dropped = excess * water;
      if (!Number.isFinite(dropped) || dropped <= 0) {
        // Guard against a non-finite cell: clamp the concentration without
        // trying to conserve a quantity that is already meaningless.
        s[b + F.sediment] = cap;
        continue;
      }
      s[b + F.sediment] = cap;
      // The excess lands as new regolith, so `height = bedrock + soil` holds.
      s[b + F.height] += dropped;
      s[b + F.soil] += dropped;
      s[b + F.bedrock] = Math.max(0, s[b + F.height] - s[b + F.soil]);
    }
  }

  /**
   * Rainfall and evaporation, applied to the live state before each advection
   * sub-step. The caller supplies that sub-step's duration, so their integrated
   * amount is independent of how finely water transport is subdivided.
   *
   * `rainfall` and `evaporation` are rates in metres per hour.
   */
  private applyRainAndEvaporation(dt: number): void {
    const hp = this.#params.hydraulic;
    const rain = (hp.rainfall / 3600) * dt;
    const evap = (hp.evaporation / 3600) * dt;
    const infiltrationRate = hp.infiltration / 3600;
    const baseflowFraction = 1 - Math.exp(-Math.max(0, hp.baseflow) * dt / 3600);
    if (rain === 0 && evap === 0 && infiltrationRate === 0 && baseflowFraction === 0) return;

    const s = this.state;
    for (let b = 0; b < s.length; b += STATE_STRIDE) {
      let groundwater = Math.max(0, s[b + F.groundwater]);
      const released = groundwater * baseflowFraction;
      groundwater -= released;
      let water = Math.max(0, s[b + F.water] + rain - evap + released);
      const soil = Math.max(0, s[b + F.soil]);
      const permeability = clamp(soil / 2, 0, 1);
      const storageHeadroom = Math.max(0, soil * 0.25 - groundwater);
      const infiltrated = Math.min(water, storageHeadroom, infiltrationRate * permeability * dt);
      s[b + F.water] = water - infiltrated;
      s[b + F.groundwater] = groundwater + infiltrated;
    }
  }
  /**
   * Sweep B: conservative transport of water and suspended sediment.
   *
   * The flux lives on **faces**, not cells: `faceX[i, j]` is the water crossing
   * between (i, j) and (i + 1, j), `faceY[i, j]` between (i, j) and (i, j + 1).
   * A boundary is shared by exactly two cells, so a cell-indexed flux field
   * aliases two different faces onto one slot and the two cells then disagree
   * about how much water crossed — which is how this solver used to manufacture
   * water out of nothing. Storing it per face makes "what the donor loses" and
   * "what the receiver gains" literally the same number.
   *
   * Every internal face is evaluated once and is limited to one eighth of its
   * donor column. A donor can feed at most four faces, so depths stay
   * non-negative while a wet cell can still advance into a dry downhill cell.
   * Boundary cells also drain into a zero-head exterior, making the height field
   * an open catchment rather than a sealed rain tank.
   */
  private waterAdvect(): void {
    const w = this.width;
    const hp = this.#params.hydraulic;
    const dt = this.#params.engine.dtSim;
    const cellSize = this.cellSize;
    const sub = scaledSubsteps(hp.flowSubsteps, dt);
    const dtSub = dt / sub;
    const conductivity = flowConductivity(hp.flowRate, hp.flowDamping, dtSub, cellSize);

    for (let step = 0; step < sub; step++) {
      // Match the GPU pass exactly: source water at the start of every transport
      // sub-step. Applying the whole tick's rain up front wets dry cells earlier
      // and changes both boundary drainage and the first sediment pickup.
      this.applyRainAndEvaporation(dtSub);
      const s = this.state;
      const dst = this.scratch;
      const faceX = this.faceX;
      const faceY = this.faceY;

      // ---- pass 1: evaluate every face once --------------------------------
      // The flux on a face is a **single** number computed from the pair of cells
      // that share it, signed by the direction the water actually flows:
      // positive means (i, j) -> (i+1, j). Evaluating it separately from each
      // cell's point of view is what used to happen, and it let the two sides
      // disagree — each reported its own velocity, the face carried the
      // difference of two independently limited quantities, and water piled up
      // on one side.
      //
      // The magnitude is limited by the **donor's** own column: it is the donor
      // that has to have the water. Limiting by the shallower of the two instead
      // lets a deep cell pour into a nearly dry neighbour, because the
      // "shallower" column would be the receiver's.
      for (let j = 0; j < w; j++) {
        for (let i = 0; i < w; i++) {
          const b = stateIndex(i, j, w);
          const water = s[b + F.water];
          const surface = s[b + F.height] + water;

          let fx = 0;
          if (i + 1 < w) {
            const nb = b + STATE_STRIDE;
            const otherWater = s[nb + F.water];
            const drop = surface - (s[nb + F.height] + otherWater);
            const donor = drop > 0 ? water : otherWater;
            if (donor > 1e-4 && Math.abs(drop) > 1e-12) {
              const u = faceVelocity(Math.abs(drop), cellSize, conductivity);
              const share = 0.125 * donor;
              fx = Math.sign(drop) * Math.min((u * dtSub * donor) / cellSize, share);
            }
          }
          faceX[j * w + i] = fx;

          let fy = 0;
          if (j + 1 < w) {
            const nb = b + w * STATE_STRIDE;
            const otherWater = s[nb + F.water];
            const drop = surface - (s[nb + F.height] + otherWater);
            const donor = drop > 0 ? water : otherWater;
            if (donor > 1e-4 && Math.abs(drop) > 1e-12) {
              const v = faceVelocity(Math.abs(drop), cellSize, conductivity);
              const share = 0.125 * donor;
              fy = Math.sign(drop) * Math.min((v * dtSub * donor) / cellSize, share);
            }
          }
          faceY[j * w + i] = fy;
        }
      }

      // ---- pass 2: apply the faces ----------------------------------------
      for (let j = 0; j < w; j++) {
        for (let i = 0; i < w; i++) {
          const b = stateIndex(i, j, w);
          const water = s[b + F.water];
          const sediment = s[b + F.sediment];

          // `faceX[j*w + i]` is signed +x between (i, j) and (i+1, j), so for
          // this cell it is an *outflow* when positive. Its -x face is the same
          // array at i-1, and for this cell that one is an inflow when positive.
          const east = faceX[j * w + i];
          const north = faceY[j * w + i];
          const west = i > 0 ? faceX[j * w + i - 1] : 0;
          const south = j > 0 ? faceY[(j - 1) * w + i] : 0;

          // An open boundary is a dry, zero-head exterior. The quarter-column
          // cap is the same as an internal face, so corner cells remain stable.
          const edgeCount = (i === 0 ? 1 : 0) + (i === w - 1 ? 1 : 0) + (j === 0 ? 1 : 0) + (j === w - 1 ? 1 : 0);
          let boundaryOut = 0;
          if (edgeCount > 0 && water > 1e-4) {
            const speed = faceVelocity(s[b + F.height] + water, cellSize, conductivity);
            const perEdge = Math.min((speed * dtSub * water) / cellSize, 0.125 * water);
            boundaryOut = perEdge * edgeCount;
          }

          let waterNext = water - east - north + west + south - boundaryOut;
          if (!Number.isFinite(waterNext) || waterNext < 0) waterNext = 0;

          // Transport suspended *load* (concentration × depth), choosing the
          // donor concentration independently on every signed face.
          let load = sediment * water;
          const eastConc = east >= 0 || i + 1 >= w ? sediment : s[b + STATE_STRIDE + F.sediment];
          const northConc = north >= 0 || j + 1 >= w ? sediment : s[b + w * STATE_STRIDE + F.sediment];
          load -= east * eastConc + north * northConc;
          if (i > 0) load += west * (west >= 0 ? s[b - STATE_STRIDE + F.sediment] : sediment);
          if (j > 0) load += south * (south >= 0 ? s[b - w * STATE_STRIDE + F.sediment] : sediment);
          load -= boundaryOut * sediment;
          let sedimentNext = waterNext > 1e-6 ? load / waterNext : 0;
          if (!Number.isFinite(sedimentNext) || sedimentNext < 0) sedimentNext = 0;
          if (sedimentNext > MAX_SEDIMENT_CONCENTRATION) sedimentNext = MAX_SEDIMENT_CONCENTRATION;

          // The untouched fields are copied only now, after every face has been
          // consumed — writing them earlier would be harmless here (the faces
          // live in their own arrays) but keeping the copy last documents the
          // invariant.
          dst.set(s.subarray(b, b + STATE_STRIDE), b);
          dst[b + F.water] = waterNext;
          dst[b + F.sediment] = sedimentNext;
          const invDepthDt = 1 / Math.max(waterNext * dtSub, 1e-6);
          // Reconstruct the cell-centred velocity from both opposing faces.
          // For uniform +x flow, west and east carry the same signed flux, so
          // their mean is the through-flow. Using only the east/north faces
          // made erosion depend on grid orientation at bends and sources.
          const xFaces = (i > 0 ? 1 : 0) + (i + 1 < w ? 1 : 0);
          const yFaces = (j > 0 ? 1 : 0) + (j + 1 < w ? 1 : 0);
          dst[b + F.velU] = ((east + west) / Math.max(1, xFaces)) * cellSize * invDepthDt;
          dst[b + F.velV] = ((north + south) / Math.max(1, yFaces)) * cellSize * invDepthDt;
        }
      }
      this.swap();
    }
  }


  /* ---------------------------------------------------------------------- */
  /* Pass 1b — multi-flow drainage accumulation                             */
  /* ---------------------------------------------------------------------- */

  /**
   * One Jacobi relaxation of upstream contributing area. Each cell contributes
   * one local unit and receives weighted contributions from higher neighbours.
   * Repeating once per tick propagates headwater area downstream without a sort
   * or atomics, and stale routes naturally flush when terrain changes.
   */
  private flowAccumulation(): void {
    const w = this.width;
    const s = this.state;
    const dst = this.scratch;
    dst.set(s);
    const slopeSum = this.flowSlopeSum;
    // First cache each donor's denominator. Computing it again for every one of
    // its possible receivers turns an 8-neighbour pass into 64 samples/cell.
    for (let j = 0; j < w; j++) {
      for (let i = 0; i < w; i++) {
        const b = stateIndex(i, j, w);
        const donorSurface = s[b + F.height] + s[b + F.water];
        let total = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const ny = j + dy;
          if (ny < 0 || ny >= w) continue;
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = i + dx;
            if (nx < 0 || nx >= w) continue;
            const nb = stateIndex(nx, ny, w);
            const drop = donorSurface - s[nb + F.height] - s[nb + F.water];
            if (drop > 0) total += drop / (dx !== 0 && dy !== 0 ? Math.SQRT2 : 1);
          }
        }
        slopeSum[j * w + i] = total;
      }
    }
    for (let j = 0; j < w; j++) {
      for (let i = 0; i < w; i++) {
        const b = stateIndex(i, j, w);
        const here = s[b + F.height] + s[b + F.water];
        let accumulated = 1;
        for (let dy = -1; dy <= 1; dy++) {
          const ny = j + dy;
          if (ny < 0 || ny >= w) continue;
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = i + dx;
            if (nx < 0 || nx >= w) continue;
            const nb = stateIndex(nx, ny, w);
            const donorSurface = s[nb + F.height] + s[nb + F.water];
            const dropHere = donorSurface - here;
            if (dropHere <= 0) continue;
            const totalSlope = slopeSum[ny * w + nx];
            if (totalSlope <= 1e-12) continue;
            const slopeHere = dropHere / (dx !== 0 && dy !== 0 ? Math.SQRT2 : 1);
            accumulated += s[nb + F.flowAccum] * slopeHere / totalSlope;
          }
        }
        dst[b + F.flowAccum] = Math.min(w * w, accumulated);
      }
    }
    this.swap();
  }


  /* ---------------------------------------------------------------------- */
  /* Pass 2 — hydraulic erosion / deposition                                */
  /* ---------------------------------------------------------------------- */

  private hydraulicErosion(): void {
    const { width: w, state: s } = this;
    const hp = this.#params.hydraulic;
    const cs = this.cellSize;
    const inv2cs = 1 / (2 * cs);
    const dt = this.#params.engine.dtSim;
    const maxPerTick = hp.maxErosionPerTick * dt / REFERENCE_DT;

    for (let j = 0; j < w; j++) {
      const jm = j > 0 ? j - 1 : 0;
      const jp = j + 1 < w ? j + 1 : w - 1;
      for (let i = 0; i < w; i++) {
        const im = i > 0 ? i - 1 : 0;
        const ip = i + 1 < w ? i + 1 : w - 1;
        const b = stateIndex(i, j, w);

        const water = s[b + F.water];
        if (water <= 1e-4) {
          s[b + F.erosion] = 0;
          s[b + F.deposition] = 0;
          continue;
        }

        // Terrain gradient by central differences (one-sided at the border).
        const gx = (s[stateIndex(ip, j, w) + F.height] - s[stateIndex(im, j, w) + F.height]) * inv2cs;
        const gy = (s[stateIndex(i, jp, w) + F.height] - s[stateIndex(i, jm, w) + F.height]) * inv2cs;
        const slope = Math.hypot(gx, gy);

        const c = s[b + F.sediment];
        const u = s[b + F.velU];
        const v = s[b + F.velV];
        const speed = Math.min(Math.hypot(u, v), MAX_WATER_SPEED);

        // Capacity is a *load* [m of sediment], so it scales with the water
        // column and a thin film cannot carry — or erode — very much.
        const capacityLoad = sedimentCapacityLoad(slope, speed, water, hp.sedimentCapacity) *
          accumulationBoost(s[b + F.flowAccum]);
        const erodibility = timeScaledFraction(
          clamp(hp.erosionStrength / Math.max(0.25, s[b + F.hardness]), 0, 1),
          dt,
        );
        const deposition = timeScaledFraction(hp.depositionRate, dt);
        const rawDelta = capacityRelaxation(c * water, capacityLoad, erodibility, deposition, maxPerTick);

        // Never dig below the datum, never move more than the per-tick cap, and
        // never act on a non-finite request. `maxPerTick` is applied here rather
        // than inside the kernel so the caller keeps control of the bound.
        const requested = Number.isFinite(rawDelta)
          ? Math.max(Math.min(rawDelta, maxPerTick), -s[b + F.height])
          : 0;

        // ---- column bookkeeping ------------------------------------------
        // The state obeys `height = bedrock + soil` exactly, so a cell's solid
        // mass is just `height` and the conserved total is
        //     total = height + suspendedLoad,  suspendedLoad = sediment * water
        //
        // Every step below is a transfer between those two terms, so the total
        // changes by exactly `delta` and nothing else. A shortfall (bed load
        // that is not lifted into suspension) thins the regolith, and once the
        // regolith is gone the same shortfall lowers the bedrock floor — which is
        // how hard bare ridges end up standing proud of soft, stripped valleys.
        const soil0 = s[b + F.soil];
        const sediment0 = s[b + F.sediment];
        const suspended0 = sediment0 * water;

        let delta = 0;
        if (requested < 0) {
          // There is no independent bed-load state. Only material that fits in
          // suspension is excavated, and the terrain loses exactly that amount.
          const headroom = Math.max(0, MAX_SEDIMENT_CONCENTRATION * water - suspended0);
          const removed = Math.min(-requested, headroom, s[b + F.height]);
          const fromSoil = Math.min(removed, soil0);
          s[b + F.soil] = soil0 - fromSoil;
          s[b + F.bedrock] = Math.max(0, s[b + F.bedrock] - (removed - fromSoil));
          s[b + F.sediment] = sediment0 + removed / water;
          delta = -removed;
        } else if (requested > 0) {
          const settled = Math.min(requested, suspended0);
          s[b + F.sediment] = sediment0 - settled / water;
          s[b + F.soil] = soil0 + settled;
          delta = settled;
        }

        if (s[b + F.sediment] < 0) s[b + F.sediment] = 0;

        // Column identity, exact by construction: `height` is recomputed from the
        // two independent thicknesses rather than integrated separately.
        s[b + F.height] = s[b + F.bedrock] + s[b + F.soil];

        s[b + F.erosion] = delta < 0 ? -delta : 0;
        s[b + F.deposition] = delta > 0 ? delta : 0;
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Pass 3 — weathering                                                     */
  /* ---------------------------------------------------------------------- */

  /**
   * Slow conversion of bedrock into mobile regolith.
   *
   *     production = k * exp(-hardness / 2) * (1 + 2 * slope) * dt
   *
   * This is the only source of *new* loose material on bare rock, which is why
   * wind erosion needs it enabled: without regolith there is nothing for the
   * wind to move.
   */
  private weathering(): void {
    const { width: w, state: s } = this;
    const cs = this.cellSize;
    const dt = this.#params.engine.dtSim;
    const inv2cs = 1 / (2 * cs);

    for (let j = 0; j < w; j++) {
      const jm = j > 0 ? j - 1 : 0;
      const jp = j + 1 < w ? j + 1 : w - 1;
      for (let i = 0; i < w; i++) {
        const im = i > 0 ? i - 1 : 0;
        const ip = i + 1 < w ? i + 1 : w - 1;
        const b = stateIndex(i, j, w);
        const bedrock = s[b + F.bedrock];
        const height = s[b + F.height];
        const soil = height - bedrock;
        const available = Math.max(0, bedrock);
        if (available <= 1e-5) continue;

        const gx = (s[stateIndex(ip, j, w) + F.height] - s[stateIndex(im, j, w) + F.height]) * inv2cs;
        const gy = (s[stateIndex(i, jp, w) + F.height] - s[stateIndex(i, jm, w) + F.height]) * inv2cs;
        const slope = Math.hypot(gx, gy);

        const hardness = s[b + F.hardness];
        const shielding = Math.exp(-soil / 2);
        const rate = 0.012 * Math.exp(-hardness / 2) * shielding * (1 + 2 * slope);
        const produced = Math.min(rate * dt, available);
        // Convert rock into regolith in place: the surface does not move, the
        // *floor* drops, so `height = bedrock + soil` still holds exactly.
        s[b + F.soil] += produced;
        s[b + F.bedrock] -= produced;
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Pass 4 — thermal (gravity) erosion                                      */
  /* ---------------------------------------------------------------------- */

  /**
   * Slope-limited mass wasting with a Jacobi sweep.
   *
   * Each sweep gathers from the 8 neighbours using the *previous* iteration's
   * heights, so the result is independent of traversal order (no directional
   * bias, no checkerboard). Total outflow from a cell is capped at its excess
   * height, which guarantees it can never fall below the neighbour it is
   * shedding material onto — that is what prevents oscillation.
   */
  private thermalErosion(): void {
    const { width: w } = this;
    const gp = this.#params.gravity;
    const limit = talusLimit(gp.talusAngle, this.cellSize);
    if (this.thermalScanDirty || this.tickCount >= this.nextThermalScanTick) {
      this.thermalMultiplier = thermalIterationMultiplier(this.state, w, limit);
      this.nextThermalScanTick = this.tickCount + 16;
      this.thermalScanDirty = false;
    }
    const baseIterations = Math.max(1, Math.round(gp.iterations));
    const iterations = scaledSubsteps(baseIterations * this.thermalMultiplier, this.#params.engine.dtSim);
    const rate = timeScaledFraction(gp.collapseRate, this.#params.engine.dtSim, iterations);

    for (let it = 0; it < iterations; it++) {
      const s = this.state;
      const dst = this.scratch;
      dst.set(s);

      for (let j = 0; j < w; j++) {
        for (let i = 0; i < w; i++) {
          const b = stateIndex(i, j, w);
          const h = s[b + F.height];
          let maxExcess = 0;
          let outflow = 0;

          for (let dj = -1; dj <= 1; dj++) {
            const nj = j + dj;
            if (nj < 0 || nj >= w) continue;
            for (let di = -1; di <= 1; di++) {
              if (di === 0 && dj === 0) continue;
              const ni = i + di;
              if (ni < 0 || ni >= w) continue;
              const hb = s[stateIndex(ni, nj, w) + F.height];
              const edgeLimit = limit * (di !== 0 && dj !== 0 ? Math.SQRT2 : 1);
              maxExcess = Math.max(maxExcess, h - hb - edgeLimit);
              outflow += thermalOutflow(h, hb, edgeLimit, rate);
            }
          }

          if (outflow <= 0) continue;
          // A receiver can collect material from all eight neighbours in this
          // Jacobi sweep. Limiting every donor to one eighth of its excess is
          // the maximum-principle bound: even an isolated one-cell pit cannot
          // be raised above the surrounding surface in one update. The old
          // one-quarter factor allowed the eight gains to add up to twice the
          // local relief, producing the thin vertical needles seen in long
          // simulations. (Bounding against the highest neighbour instead
          // makes outflow zero on a flat plateau, which is a common real case.)
          const budget = Math.max(0, maxExcess) * 0.125;
          const total = Math.min(outflow, budget);
          if (total <= 1e-9) continue;
          const scale = total / outflow;

          // Remove the donor's total once. Loose cover is consumed first; any
          // rock shortfall becomes loose debris at the receivers rather than
          // remaining classified as intact bedrock after falling.
          const lossSoil = Math.min(total, s[b + F.soil]);
          const lossRock = total - lossSoil;
          dst[b + F.height] -= total;
          dst[b + F.soil] -= lossSoil;
          dst[b + F.bedrock] -= lossRock;

          for (let dj = -1; dj <= 1; dj++) {
            const nj = j + dj;
            if (nj < 0 || nj >= w) continue;
            for (let di = -1; di <= 1; di++) {
              if (di === 0 && dj === 0) continue;
              const ni = i + di;
              if (ni < 0 || ni >= w) continue;
              const nb = stateIndex(ni, nj, w);
              const edgeLimit = limit * (di !== 0 && dj !== 0 ? Math.SQRT2 : 1);
              const move = thermalOutflow(h, s[nb + F.height], edgeLimit, rate) * scale;
              if (move <= 0) continue;
              dst[nb + F.height] += move;
              dst[nb + F.soil] += move;
            }
          }
        }
      }
      this.swap();
      // One donor can feed several neighbours in the same Jacobi sweep. The
      // edge-wise loose-material preference may therefore make the transient
      // soil field negative even though height remains conservative. Do not
      // feed that invalid intermediate state into the next relaxation sweep:
      // re-establish the column invariant after every global barrier.
      this.clampState();
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Pass 5 — wind erosion                                                  */
  /* ---------------------------------------------------------------------- */

  /**
   * Wind erosion: shear entrainment of loose material plus shadow deposition.
   *
   * Only the regolith moves — the wind cannot quarry bedrock, which is why dunes
   * need either weathering or the sediment brush to get started.
   *
   * Structure: one entrainment sweep collects every cell's shed material into a
   * work list, then one deposition sweep hands it downwind. Splitting the two
   * means a grain cannot be eroded twice in a single tick, and the material
   * balance is exact:
   *
   *     shed_i = deposited + exported
   *
   * The exported remainder is accumulated in each source cell's persistent
   * `windExport` counter, so `solid mass + exported mass` is conserved.
   */
  private windErosion(): void {
    const { width: w } = this;
    const wp = this.#params.wind;
    const cs = this.cellSize;
    const dt = this.#params.engine.dtSim;
    const theta = (wp.direction * Math.PI) / 180;
    const wx = Math.cos(theta);
    const wy = Math.sin(theta);
    const hop = Math.max(1, wp.transportDistance);
    const dipX = Math.round(wx * hop);
    const dipY = Math.round(wy * hop);

    // --- entrainment sweep ------------------------------------------------
    // `moved` is a flat (i, j, amount) triple list, reused across ticks so the
    // pass allocates nothing per frame.
    const moved = this.movedScratch;
    moved.length = 0;
    const source = this.state;
    const target = this.scratch;
    target.set(source);
    for (let j = 0; j < w; j++) {
      for (let i = 0; i < w; i++) {
        const b = stateIndex(i, j, w);
        const loose = source[b + F.soil];
        if (loose <= 1e-6 || wp.speed <= WIND_THRESHOLD) continue;

        const shelter = windShelter(source[b + F.height], this.sampleUpwind(source, i, j, wx, wy), cs);
        const exposure = 1 - shelter;

        // Windward-facing slope: positive when the ground rises into the wind.
        const upH = this.sampleUpwindBilinear(source, i, j, wx, wy);
        const windwardRise = (source[b + F.height] - upH) / (cs * hop);

        const amount = Math.min(windEntrainment(loose, wp.speed, exposure, windwardRise, wp.erosion, dt), loose);
        if (amount <= 0) continue;
        moved.push(i, j, amount);
        // Only the surface layer moves: the terrain thins by exactly the amount
        // that enters the load. `soil` is the independent state here and
        // `bedrock = height - soil` is re-derived, which keeps the column
        // identity exact without ever clamping mass away.
        target[b + F.soil] = loose - amount;
        // `bedrock` is left untouched: the wind can only deflate the regolith
        // that lies on top of it, never the rock itself. Because `amount` is
        // clamped to `loose`, the column thins to exactly the bedrock floor and
        // stops there.
        target[b + F.height] = target[b + F.bedrock] + target[b + F.soil];
      }
    }

    // --- downwind deposition, trapped in wind shadows ---------------------
    // Conservation is enforced by construction: every grain in `moved` is
    // either deposited (raising the surface where it lands) or added to that
    // source cell's export counter on its way out of the domain. Nothing is
    // ever clamped away, because
    // `soil` is the independent state and `bedrock = height - soil` is
    // re-derived — clamping the regolith instead would silently destroy mass.
    const maxHops = 8;
    for (let m = 0; m < moved.length; m += 3) {
      const i = moved[m];
      const j = moved[m + 1];
      const amount = moved[m + 2];
      let ci = i;
      let cj = j;
      let carry = amount;
      for (let step = 1; step <= maxHops && carry > 1e-9; step++) {
        const di = ci + dipX;
        const dj = cj + dipY;
        if (di < 0 || dj < 0 || di >= w || dj >= w) {
          target[stateIndex(i, j, w) + F.windExport] += carry;
          carry = 0;
          break;
        }
        const db = stateIndex(di, dj, w);
        const shelter = windShelter(source[db + F.height], this.sampleUpwind(source, di, dj, wx, wy), cs);
        const hopFraction = step === maxHops ? 1 : windDepositFraction(shelter, wp.deposition, dt);
        const deposit = carry * hopFraction;
        target[db + F.soil] += deposit;
        target[db + F.height] = target[db + F.bedrock] + target[db + F.soil];
        carry -= deposit;
        ci = di;
        cj = dj;
      }
    }
    this.swap();
  }

  /**
   * Terrain height 1, 3 and 8 cells upwind of (i, j), bilinearly interpolated so
   * the shelter metric is independent of the (continuous) transport distance.
   * Cells outside the grid fall back to the local height, so they shelter
   * nothing.
   */
  private sampleUpwind(s: Float32Array, i: number, j: number, wx: number, wy: number): Float32Array {
    const out = this.upwindScratch;
    for (let k = 0; k < 3; k++) {
      const d = k === 0 ? 1 : k === 1 ? 3 : 8;
      out[k] = this.sampleUpwindBilinear(s, i, j, wx, wy, d);
    }
    return out;
  }

  /** Terrain height at `distance` cells upwind of (i, j). */
  private sampleUpwindBilinear(
    s: Float32Array,
    i: number,
    j: number,
    wx: number,
    wy: number,
    distance = 1,
  ): number {
    const w = this.width;
    const gi = i - wx * distance;
    const gj = j - wy * distance;
    const fi = clamp(gi, 0, w - 1.0001);
    const fj = clamp(gj, 0, w - 1.0001);
    const i0 = Math.floor(fi);
    const j0 = Math.floor(fj);
    const tx = fi - i0;
    const ty = fj - j0;
    const i1 = Math.min(i0 + 1, w - 1);
    const j1 = Math.min(j0 + 1, w - 1);
    const b00 = stateIndex(i0, j0, w) + F.height;
    const b10 = stateIndex(i1, j0, w) + F.height;
    const b01 = stateIndex(i0, j1, w) + F.height;
    const b11 = stateIndex(i1, j1, w) + F.height;
    return (
      s[b00] * (1 - tx) * (1 - ty) +
      s[b10] * tx * (1 - ty) +
      s[b01] * (1 - tx) * ty +
      s[b11] * tx * ty
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Buffer plumbing                                                        */
  /* ---------------------------------------------------------------------- */

  private swap(): void {
    const tmp = this.state;
    this.state = this.scratch;
    this.scratch = tmp;
  }

  /* ---------------------------------------------------------------------- */
  /* Stats                                                                  */
  /* ---------------------------------------------------------------------- */

  stats(): SimStats {
    const now = performance.now();
    if (this.lastFrameStamp > 0) {
      const frame = now - this.lastFrameStamp;
      this.frameTimeMs = frame;
      this.fps = frame > 0 ? 1000 / frame : 0;
    }
    this.lastFrameStamp = now;

    const { width: w, state: s } = this;
    let waterTotal = 0;
    let sedimentTotal = 0;
    let looseTotal = 0;
    let terrainSum = 0;
    let minH = Infinity;
    let maxH = -Infinity;
    let windLossTotal = 0;

    for (let k = 0; k < w * w; k++) {
      const b = k * STATE_STRIDE;
      const water = s[b + F.water];
      waterTotal += water + s[b + F.groundwater];
      sedimentTotal += s[b + F.sediment];
      looseTotal += s[b + F.soil];
      windLossTotal += s[b + F.windExport];
      const h = s[b + F.height];
      terrainSum += h;
      if (h < minH) minH = h;
      if (h > maxH) maxH = h;
    }

    return {
      tick: this.tickCount,
      simTime: this.simTime,
      fps: this.fps,
      frameTimeMs: this.frameTimeMs,
      simTimeMs: this.simMs,
      renderTimeMs: 0,
      waterTotal,
      sedimentTotal,
      looseTotal,
      terrainSum,
      meanHeight: terrainSum / (w * w),
      minHeight: minH,
      maxHeight: maxH,
      peakHeight: maxH,
      windLossTotal,
      gridWidth: w,
      gridHeight: w,
      backend: 'CPU (typed arrays)',
    };
  }

  /* ---------------------------------------------------------------------- */
  /* Data in / out                                                          */
  /* ---------------------------------------------------------------------- */

  async readState(): Promise<Float32Array> {
    return this.state.slice();
  }

  /**
   * Overwrite the state *and* adopt it as the baseline that
   * "Reset simulation" restores. Used by heightmap import and world load.
   */
  writeState(data: Float32Array): void {
    if (data.length !== this.state.length) throw new Error('state size mismatch');
    this.state.set(data);
    this.initial.set(data);
  }

  /**
   * Overwrite only the *live* state, leaving the reset baseline untouched.
   * Used by scenario setup, undo and brush strokes.
   */
  loadState(data: Float32Array): void {
    if (data.length !== this.state.length) throw new Error('state size mismatch');
    this.state.set(data);
  }

  /* ---------------------------------------------------------------------- */
  /* Brush                                                                  */
  /* ---------------------------------------------------------------------- */

  applyBrush(stroke: BrushStroke): boolean {
    const touched = applyBrushToState(this.state, this.width, this.cellSize, stroke);
    if (touched) this.thermalScanDirty = true;
    return touched;
  }

  endStroke(): void {
    /* the CPU backend mutates state in place, nothing to flush */
  }

  destroy(): void {
    /* no resources owned */
  }
}

